import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import "./styles.scss";
import Nav from "./Nav";
import Footer from "./Footer";
import { Pill, LiveDot, ButtonPrimary, SectionLabel } from "./ui";

import { Helmet } from "react-helmet-async";

const VALUES = [
  { t: "Outcomes over slides", d: "We measure ourselves by what your business changes, not what we present." },
  { t: "Co-design with operators", d: "Every agent is built with the people who'll work alongside it. No throw-it-over-the-wall." },
  { t: "Compounding leverage", d: "Each agent makes the next one easier. Your AI fabric gets stronger every quarter." },
  { t: "Trust as infrastructure", d: "Audit trails, guardrails, role-based access and explainability — baked in, not bolted on." },
];

const OFFICES = [
  { flag: "🇺🇸", label: "USA Office",       lines: ["1111b South Governors Avenue", "Dover, DE, 19904", "United States"] },
  { flag: "🇸🇬", label: "Singapore Office", lines: ["60 Paya Lebar Road", "#06-28 Paya Lebar Square", "Singapore 409051"] },
  { flag: "🇧🇩", label: "Bangladesh Office",lines: ["13th Floor, Razzak Plaza", "Mog Bazar Moor", "Dhaka-1205, Bangladesh"] },
];

const Field = ({ label, placeholder, type = "text", textarea = false }) => (
  <label className="block">
    <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-white/45">{label}</span>
    {textarea
      ? <textarea rows="4" placeholder={placeholder} className="mt-1.5 w-full rounded-lg border border-white/[0.08] bg-[#070d24] px-3 py-2.5 text-[13.5px] text-white placeholder-white/30 outline-none focus:border-cyan-300/40" />
      : <input type={type} placeholder={placeholder} className="mt-1.5 w-full rounded-lg border border-white/[0.08] bg-[#070d24] px-3 py-2.5 text-[13.5px] text-white placeholder-white/30 outline-none focus:border-cyan-300/40" />
    }
  </label>
);

const About = () => (
  <section id="about" className="scroll-mt-24 relative pt-32 pb-16 lg:pt-40">
    <div className="pointer-events-none absolute inset-0 bg-grid opacity-50" />
    <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-[420px] w-[1000px] rounded-full blur-[120px] opacity-40"
         style={{ background: "radial-gradient(closest-side, rgba(76,125,255,0.45), rgba(94,234,212,0.15), transparent 70%)" }} />
    <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
      <Pill tone="accent"><LiveDot /> &nbsp; Our company</Pill>
      <h1 className="mt-5 max-w-4xl text-[44px] leading-[1.02] tracking-tight sm:text-[58px] lg:text-[64px]">
        <span className="text-white">We turn back-office labor into</span>{" "}
        <span className="font-display italic grad-text">self-learning software.</span>
      </h1>

      <div className="mt-10 max-w-3xl">
        <div className="space-y-5 text-[16.5px] leading-relaxed text-white/75">
          <p>EkkBaz AI was born inside the operational chaos we now automate. We started by helping FMCG distributors collect receivables, then realized the same self-learning engine could replace whole categories of operational labor &mdash; across procurement, supply chain, customer experience and finance.</p>
          <p>We&rsquo;re not a chatbot vendor. We&rsquo;re a small team of AI engineers and industry operators who build custom agents end-to-end &mdash; and stay accountable to the KPIs they&rsquo;re supposed to move.</p>
        </div>
      </div>

      <div className="mt-16">
        <div className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-cyan-300/80">How we operate</div>
        <div className="mt-5 grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          {VALUES.map(v => (
            <div key={v.t} className="rounded-2xl border border-white/[0.06] bg-white/[0.025] p-6">
              <div className="text-[17px] font-semibold text-white">{v.t}</div>
              <p className="mt-2 text-[13.5px] leading-relaxed text-white/60">{v.d}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Contact = () => (
  <section id="contact" className="scroll-mt-24 relative py-16 border-t border-white/[0.06]">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="grid items-start gap-10 lg:grid-cols-2">
        <div>
          <SectionLabel tone="accent">Contact</SectionLabel>
          <h2 className="mt-5 text-[32px] leading-[1.05] tracking-tight sm:text-[40px]">
            <span className="text-white">Real architects.</span><br/>
            <span className="font-display italic grad-text">Not BDRs.</span>
          </h2>
          <p className="mt-5 max-w-md text-[15px] leading-relaxed text-white/65">
            Tell us about the workflow that&rsquo;s killing your margin. We&rsquo;ll come back inside two business days with a costed plan &mdash; not a generic deck.
          </p>

          <div className="mt-8 space-y-3 text-[14px]">
            <div className="flex items-center gap-3">
              <span className="grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-cyan-300">@</span>
              <a href="mailto:sales@ekkbaz.ai" className="text-white hover:text-cyan-200">sales@ekkbaz.ai</a>
            </div>
            <div className="flex items-center gap-3">
              <span className="grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-cyan-300">℡</span>
              <a href="https://wa.me/+6592344538" className="text-white hover:text-cyan-200">+65 9234 4538 (WhatsApp)</a>
            </div>
          </div>

          <div className="mt-8 grid gap-3 sm:grid-cols-3">
            {OFFICES.map(o => (
              <div key={o.label} className="rounded-2xl border border-white/[0.08] bg-white/[0.025] p-4">
                <div className="flex items-center gap-2">
                  <span className="text-[16px]">{o.flag}</span>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-300/80">{o.label}</div>
                </div>
                <div className="mt-2 text-[13px] leading-[1.55] text-white/75">
                  {o.lines.map((l, i) => <div key={i}>{l}</div>)}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <ButtonPrimary href="https://bookings.cloud.microsoft/book/EkkBazInvestment@EkkBaz.com/?ismsaljsauthenabled" external>Book a strategy call</ButtonPrimary>
          </div>
        </div>

        <form className="rounded-2xl border border-white/[0.08] bg-white/[0.025] p-7" onSubmit={e => { e.preventDefault(); alert("Thanks — we'll be in touch within two business days."); }}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Your name" placeholder="Jane Doe" />
            <Field label="Work email" placeholder="jane@company.com" type="email" />
            <Field label="Company" placeholder="Acme Global" />
            <Field label="Role" placeholder="VP Operations" />
          </div>
          <Field label="Workflow you'd like to automate" placeholder="e.g. AR collections, supplier onboarding…" />
          <Field label="Tell us more" placeholder="Volumes, current tools, team size, KPIs you want to move" textarea />
          <div className="mt-5 flex items-center justify-between gap-4">
            <p className="text-[11.5px] text-white/40">No spam · one reply · then it&rsquo;s up to you.</p>
            <button type="submit" className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-[13.5px] font-semibold text-[#051030] hover:-translate-y-0.5">
              Send
              <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          </div>
        </form>
      </div>
    </div>
  </section>
);

function useHashScroll() {
  const loc = useLocation();
  useEffect(() => {
    if (!loc.hash) return;
    const el = document.getElementById(loc.hash.replace("#", ""));
    if (el) requestAnimationFrame(() => window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: "smooth" }));
  }, [loc.hash, loc.pathname]);
}

export default function CompanyPage() {
  useHashScroll();
  return (
    <div className="ai-root min-h-screen" data-screen-label="EkkBaz AI · Company">
      <Helmet>
        <title>Company — EkkBaz AI | About Us, Mission & Contact</title>
        <meta name="description" content="EkkBaz AI turns operational labor into self-learning software. Learn about our mission, values and team. Offices in USA, Singapore and Bangladesh. Contact: sales@ekkbaz.ai" />
        <link rel="canonical" href="https://ekkbaz.ai/company" />
        <meta property="og:title" content="Company — EkkBaz AI | About & Contact" />
        <meta property="og:description" content="EkkBaz AI turns operational labor into self-learning software. Offices in USA, Singapore and Bangladesh." />
        <meta property="og:url" content="https://ekkbaz.ai/company" />
        <meta property="og:image" content="https://ekkbaz.ai/assets/images/ekkbaz-og.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "url": "https://ekkbaz.ai/company",
          "name": "About EkkBaz AI",
          "description": "EkkBaz AI turns operational labor into self-learning software. Offices in USA, Singapore and Bangladesh.",
          "isPartOf": {"@id": "https://ekkbaz.ai/#website"},
          "about": {"@id": "https://ekkbaz.ai/#organization"}
        })}</script>
      </Helmet>
      <Nav />
      <main>
        <About />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}
